import {
  Button,
  DebouncedTextField,
  Flex,
  MagnifyingGlassIcon,
  Panel,
  PlusIcon,
  ProgressCircle,
  Text,
} from 'components'
import pluralize from 'pluralize'
import { useState } from 'react'
import { useBreakpoint, useTheme } from 'utils'
import { ContentRecord } from '../../utils/contentUtils'
import ContentTile from '../ContentTile'

interface Props {
  content?: ContentRecord[]
  onEdit?: (id: string) => void
  onSelect?: (id: string) => void
  onChildrenSelect?: (id: string) => void
  onCreate?: () => void
  status: 'error' | 'success' | 'pending'
}

export default function TextContentList({
  content,
  onEdit,
  onSelect,
  onChildrenSelect,
  onCreate,
  status,
}: Props) {
  const { theme } = useTheme()
  const [search, setSearch] = useState('')
  const isMobile = useBreakpoint() < 1

  function performSearch(val: string): void {
    setSearch(val)
  }

  if (status === 'pending') {
    return (
      <Flex p={20} justifyContent="center" maxW={'100%'}>
        <ProgressCircle isIndeterminate />
      </Flex>
    )
  }

  if (!content) {
    return (
      <Flex p={20} justifyContent="center" maxW={'100%'}>
        <Text>No content available</Text>
      </Flex>
    )
  }

  return (
    <Flex mb={2} direction="column" flex={1} gap={2} overflow="auto" pr={isMobile ? 0 : 5}>
      <Flex direction="row" gap={2} justify="space-between" p={1}>
        <DebouncedTextField
          disabled={content.length === 0}
          onChange={performSearch}
          placeholder="Search..."
          value={search}
          leftElement={
            <MagnifyingGlassIcon color={theme === 'light' ? 'rgba(23, 23, 23, 0.6)' : 'white'} />
          }
          width={{ base: '200px', sm: '300px' }}
          controlled
          boxShadow={'0px 1px 4px 0px rgba(0, 0, 0, 0.06)'}
          backdropFilter={'blur(14px)'}
          bg={theme === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
          borderRadius="sm"
          borderWidth={0}
          pl={10}
        />
        {onCreate && (
          <Panel>
            <Button leftIcon={<PlusIcon />} variant="ghost" onClick={onCreate}>
              Add Text
            </Button>
          </Panel>
        )}
      </Flex>

      <Flex direction="column" gap={2}>
        {content
          .filter(
            item =>
              item.title?.toLowerCase().includes(search.toLowerCase()) ||
              item.value.en?.toLowerCase().includes(search.toLowerCase())
          )
          .map(item => (
            <ContentTile
              key={item.id}
              onClick={onSelect ? () => onSelect?.(item.id) : undefined}
              onEdit={onEdit ? () => onEdit?.(item.id) : undefined}
              infoTextProps={
                onChildrenSelect ? { onClick: () => onChildrenSelect?.(item.id) } : undefined
              }
              title={item.title}
              description={item.value.en}
              infoText={
                item.childCount
                  ? `${item.childCount} Sub Content ${pluralize('Item', item.childCount)}`
                  : undefined
              }
            />
          ))}
      </Flex>
    </Flex>
  )
}
